/**
 * Static Before / After charting cards for the "All" visual overview (PRD.166).
 *
 * Renders one split-thumbnail card per saved charting record (newest first).
 * Each card is left/right (Before / After), visually distinct from raw image
 * thumbnails, and consistent in sizing with the surrounding grid.
 *
 * Both photo and drawing references resolve through the shared compare-asset
 * pipeline (PRD.235), so drawing-side halves render correctly.
 *
 * These are static preview cards — no interactive slider.
 * Clicking opens the focused Before / After workspace for that pair.
 */

import { usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { qEncounterVisualCharts, type EncounterVisualChartRow } from '@helios/data-access';
import {
  AssetImage,
  chartHasAnyRef,
  pickedFromChart,
  pickedKey,
  usePatientCompareAssets,
  type CompareAsset,
} from './compare-asset-utils';

interface Props {
  patientId: string;
  encounterId: string;
  onOpen?: (chartId: string) => void;
}

export function SavedBeforeAfterPreview({ patientId, encounterId, onOpen }: Props) {
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: chartRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId),
  );
  // Patient-wide so saved cross-visit pairs (PRD.253) resolve their thumbnails.
  const { assetByKey } = usePatientCompareAssets(patientId, encounterId, orgId);

  const charts = chartRows.filter(chartHasAnyRef);

  if (charts.length === 0) return null;

  return (
    <>
      {charts.map((chart) => {
        const beforeKey = pickedKey(pickedFromChart(chart, 'before'));
        const afterKey = pickedKey(pickedFromChart(chart, 'after'));
        const beforeAsset = beforeKey ? (assetByKey.get(beforeKey) ?? null) : null;
        const afterAsset = afterKey ? (assetByKey.get(afterKey) ?? null) : null;

        return (
          <ChartingCard
            key={chart.id}
            beforeAsset={beforeAsset}
            afterAsset={afterAsset}
            onClick={onOpen ? () => onOpen(chart.id) : undefined}
          />
        );
      })}
    </>
  );
}

function ChartingCard({
  beforeAsset,
  afterAsset,
  onClick,
}: {
  beforeAsset: CompareAsset | null;
  afterAsset: CompareAsset | null;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      title="Open Before / After"
      style={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
        cursor: onClick ? 'pointer' : 'default',
        padding: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        width: '100%',
      }}
    >
      <Half asset={beforeAsset} fallback="B" />
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: 2,
          background: 'rgba(255,255,255,0.7)',
          zIndex: 2,
        }}
      />
      <Half asset={afterAsset} fallback="A" />
      <span
        style={{
          position: 'absolute',
          bottom: 4,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '1px 8px',
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          zIndex: 3,
        }}
      >
        Before / After
      </span>
    </button>
  );
}

function Half({ asset, fallback }: { asset: CompareAsset | null; fallback: string }) {
  if (!asset) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
        }}
      >
        {fallback}
      </div>
    );
  }
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <AssetImage
        asset={asset}
        alt={fallback}
        objectFit={asset.source === 'drawing' ? 'contain' : 'cover'}
      />
    </div>
  );
}
